// ===========================================
// CreditNova Generate Page
// ===========================================
//
// Route: /workflow/generate
// Purpose: Dedicated page for the report generation pipeline.
//          Receives control after upload.tsx (no-financial flow) or
//          metrics.tsx (financial flow). Single responsibility:
//          wait for all non-FS docs to be terminal → extract →
//          cross-period analysis → call /generate → SSE+poll → redirect.
//
// This page eliminates the fragile re-mount of upload.tsx after
// overrides/metrics. It mounts once when it's time to generate,
// runs the pipeline linearly, and navigates to the analysis page.
//
// ===========================================

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useWorkflow, type DocumentStatus } from '@/contexts/WorkflowContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Sparkles,
  XCircle,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api-client';
import { getWriteupType } from '@/lib/writeup-type-util';

// ===========================================
// Route Registration
// ===========================================

export const Route = createFileRoute('/workflow/_layout/generate')({
  component: GenerateStep,
});

// ===========================================
// Helper: Sort financial extractions by fiscal period
// ===========================================

function sortByLatestPeriod<T extends { data: any; period?: string }>(extractions: T[]): T[] {
  if (extractions.length <= 1) return extractions;
  return [...extractions].sort((a, b) => {
    const yearA = extractYear(a.data?.periodEnding || a.period || '');
    const yearB = extractYear(b.data?.periodEnding || b.period || '');
    return yearB - yearA;
  });
}

function extractYear(periodStr: string): number {
  const match = String(periodStr).match(/\b(19|20)\d{2}\b/);
  return match ? parseInt(match[0], 10) : 0;
}

// ===========================================
// Module-Level Dedup Guard
// ===========================================
// Prevents duplicate extraction API calls across React StrictMode mounts.
const extractedDocIdsGlobal = new Set<string>();

// ===========================================
// Pipeline Phase
// ===========================================
type GeneratePipelinePhase =
  | 'waiting_for_docs'
  | 'extracting_rest'
  | 'cross_period'
  | 'generating'
  | 'complete'
  | 'error';

// ===========================================
// GenerateStep Component
// ===========================================

function GenerateStep() {
  const {
    setCurrentStep,
    updateData,
    data,
    markStepComplete,
    documentStatuses,
    activityLog,
    addActivity,
    closeSse,
    waitForEarlyExtractions,
    reportResult,
    reportError,
  } = useWorkflow();
  const { toast } = useToast();
  const navigate = useNavigate();

  // ---------------------------------------------------------------
  // State
  // ---------------------------------------------------------------
  const [pipelinePhase, setPipelinePhase] = useState<GeneratePipelinePhase>('waiting_for_docs');
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [expandedIntegrity, setExpandedIntegrity] = useState<Set<string>>(new Set());

  // Refs
  const activityLogRef = useRef<HTMLDivElement | null>(null);
  const reportPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reportNavigatedRef = useRef(false);
  // Guard: ensures the extraction+generate pipeline runs exactly once
  const pipelineStartedRef = useRef(false);

  // Seed dedup set with early-extracted doc IDs
  for (const e of (data.earlyExtractions || []) as Array<{ documentId: string }>) {
    extractedDocIdsGlobal.add(e.documentId);
  }

  // ---------------------------------------------------------------
  // Sync StageTracker step
  // ---------------------------------------------------------------
  useEffect(() => {
    setCurrentStep('generating');
  }, [setCurrentStep]);

  // ---------------------------------------------------------------
  // Auto-scroll activity log
  // ---------------------------------------------------------------
  useEffect(() => {
    requestAnimationFrame(() => {
      activityLogRef.current?.scrollTo({ top: activityLogRef.current.scrollHeight, behavior: 'smooth' });
    });
  }, [activityLog.length]);

  // ---------------------------------------------------------------
  // Determine if this workflow includes financial statements
  // ---------------------------------------------------------------
  const hasFinancialDocs = useMemo(
    () => data.uploadedDocuments.some((d) => d.documentType === 'financial_statement'),
    [data.uploadedDocuments],
  );

  // ---------------------------------------------------------------
  // Report completion handler — shared by SSE and polling paths.
  // Uses a ref guard to prevent double navigation.
  // ---------------------------------------------------------------
  const handleReportComplete = useCallback((analysisId: string, reportId?: string) => {
    if (reportNavigatedRef.current) return;
    reportNavigatedRef.current = true;

    if (reportPollRef.current) {
      clearInterval(reportPollRef.current);
      reportPollRef.current = null;
    }

    updateData({
      reportId: reportId || analysisId,
      workflowState: 'complete',
    });

    markStepComplete('upload');
    markStepComplete('report');

    setPipelinePhase('complete');
    addActivity('report.gen', `Report complete — #${analysisId}`, 'success');
    closeSse();

    toast({
      title: 'Report Generated',
      description: 'Your credit analysis report is ready to view.',
    });

    setTimeout(() => {
      navigate({ to: '/analyses/$id', params: { id: analysisId } });
    }, 1500);
  }, [updateData, markStepComplete, addActivity, closeSse, toast, navigate]);

  // ---------------------------------------------------------------
  // SSE-driven report completion
  // ---------------------------------------------------------------
  useEffect(() => {
    if (reportResult && pipelinePhase === 'generating') {
      handleReportComplete(reportResult.analysisId, reportResult.reportId);
    }
  }, [reportResult, pipelinePhase, handleReportComplete]);

  useEffect(() => {
    if (reportError && pipelinePhase === 'generating') {
      if (reportNavigatedRef.current) return;
      reportNavigatedRef.current = true;

      if (reportPollRef.current) {
        clearInterval(reportPollRef.current);
        reportPollRef.current = null;
      }

      setPipelinePhase('error');
      setPipelineError(reportError);
      closeSse();
      toast({
        title: 'Processing Failed',
        description: reportError,
        variant: 'destructive',
      });
    }
  }, [reportError, pipelinePhase, closeSse, toast]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (reportPollRef.current) {
        clearInterval(reportPollRef.current);
      }
    };
  }, []);

  // ---------------------------------------------------------------
  // Handle resume: if already generating or complete, skip pipeline
  // ---------------------------------------------------------------
  useEffect(() => {
    if (data.workflowState === 'complete' && data.analysisId) {
      navigate({ to: '/analyses/$id', params: { id: data.analysisId } });
      return;
    }

    if (data.workflowState === 'generating') {
      setPipelinePhase('generating');
      // Start polling for report completion
      reportPollRef.current = setInterval(async () => {
        try {
          const status = await api.reports.status(data.workflowId);
          if ((status.status === 'complete' || status.status === 'completed') && status.analysisId) {
            handleReportComplete(status.analysisId, status.reportId);
          }
        } catch {
          // Polling errors are non-fatal — SSE is the primary channel
        }
      }, 10_000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------
  // Core pipeline: wait for docs → extract → cross-period → generate
  //
  // Watches documentStatuses. Once ALL expected non-FS docs are
  // terminal (completed or failed), runs the extraction + generation
  // pipeline exactly once (guarded by pipelineStartedRef).
  // ---------------------------------------------------------------
  useEffect(() => {
    // Don't run if already started, or if in generating/complete state
    if (pipelineStartedRef.current) return;
    if (data.workflowState === 'generating' || data.workflowState === 'complete') return;

    const knownDocIds = new Set(data.uploadedDocuments.map((d) => d.id));
    const relevantStatuses = documentStatuses.filter((d) => knownDocIds.has(d.id));

    // Determine which docs we're waiting for
    const nonFsDocs = relevantStatuses.filter((d) => d.documentType !== 'financial_statement');
    const expectedNonFsCount = data.uploadedDocuments.filter(
      (d) => d.documentType !== 'financial_statement'
    ).length;

    // For no-financial flow, wait for ALL docs; for financial flow, wait for non-FS docs
    if (!hasFinancialDocs) {
      // No-financial flow: wait for all docs to be terminal
      const allTerminal = relevantStatuses.length >= data.uploadedDocuments.length &&
        relevantStatuses.length > 0 &&
        relevantStatuses.every((d) => d.status === 'completed' || d.status === 'failed');
      if (!allTerminal) {
        setPipelinePhase('waiting_for_docs');
        return;
      }
    } else {
      // Financial flow: wait for all non-FS docs to be terminal
      if (expectedNonFsCount === 0) {
        // No non-FS docs (e.g., single financial statement) — proceed immediately
      } else if (
        nonFsDocs.length >= expectedNonFsCount &&
        nonFsDocs.length > 0 &&
        nonFsDocs.every((d) => d.status === 'completed' || d.status === 'failed')
      ) {
        // All non-FS docs terminal — proceed
      } else {
        setPipelinePhase('waiting_for_docs');
        return;
      }
    }

    // All expected docs are terminal — start the pipeline
    pipelineStartedRef.current = true;
    const completedNonFsDocs = nonFsDocs.filter((d) => d.status === 'completed');
    runPipeline(completedNonFsDocs);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentStatuses, data.workflowState, hasFinancialDocs]);

  // ---------------------------------------------------------------
  // Pipeline execution
  // ---------------------------------------------------------------
  const runPipeline = async (completedNonFsDocs: DocumentStatus[]) => {
    setPipelinePhase('extracting_rest');
    addActivity('pipeline', 'Extracting remaining documents...', 'info');

    try {
      // Extract non-financial documents
      // Use early extractions from WorkflowContext when available
      const extractionResults: Array<{
        documentType: string;
        documentId: string;
        data: any;
      }> = [];

      // Wait for any in-progress early extractions to finish
      const earlyResults = await waitForEarlyExtractions();

      for (const early of earlyResults) {
        extractionResults.push({
          documentType: early.documentType,
          documentId: early.documentId,
          data: early.data,
        });
        extractedDocIdsGlobal.add(early.documentId);
        addActivity('extractor', `${early.documentId} — using cached extraction`, 'success');
      }

      // Only extract docs that weren't early-extracted
      const earlyDocIds = new Set(earlyResults.map((e) => e.documentId));
      const docsNeedingExtraction = completedNonFsDocs.filter((d) => !earlyDocIds.has(d.id));

      for (const doc of docsNeedingExtraction) {
        if (extractedDocIdsGlobal.has(doc.id)) {
          addActivity('extractor', `${doc.name} — already extracted, skipping`, 'info');
          continue;
        }
        extractedDocIdsGlobal.add(doc.id);

        try {
          addActivity('extractor', `Analyzing ${doc.name}...`, 'info');
          const result = await api.extraction.extract({
            uploadId: doc.id,
            workflowId: data.workflowId,
            period: 'annual',
            detailLevel: 'detailed',
          });
          // Unmask PII tokens for display
          let displayData = result.data;
          if (result.extractionId) {
            try {
              const unmasked = await api.extraction.unmask({
                extractionIds: [result.extractionId],
                data: result.data,
              });
              displayData = unmasked.data as typeof result.data;
            } catch {
              // Non-fatal: fall back to masked data
            }
          }
          extractionResults.push({
            documentType: doc.documentType,
            documentId: doc.id,
            data: displayData,
          });
          addActivity('extractor', `${doc.name} — extraction complete`, 'success');
        } catch (err) {
          console.error(`Extraction failed for ${doc.name}:`, err);
          addActivity('extractor', `${doc.name} — extraction failed`, 'error');
          extractedDocIdsGlobal.delete(doc.id);
        }
      }

      // Collect all extractions by type (non-FS)
      const creditReportExtractions: Array<{ data: any; documentId: string; period: string }> = [];
      const bankStatementExtractions: Array<{ data: any; documentId: string; period: string }> = [];
      const corporateExtractions: Array<{ data: any; documentId: string; period: string }> = [];

      for (const result of extractionResults) {
        const uploadedDoc = data.uploadedDocuments.find((d) => d.id === result.documentId);
        const period = uploadedDoc?.period || 'Annual';

        switch (result.documentType) {
          case 'credit_report':
          case 'equifax':
            creditReportExtractions.push({ data: result.data, documentId: result.documentId, period });
            break;
          case 'bank_statement':
            bankStatementExtractions.push({ data: result.data, documentId: result.documentId, period });
            break;
          case 'corporate_search':
            corporateExtractions.push({ data: result.data, documentId: result.documentId, period });
            break;
        }
      }

      // Use the overridden financial data if available, otherwise raw extractions
      const financialExtractions = data.overriddenFinancialExtractions.length > 0
        ? data.overriddenFinancialExtractions
        : data.financialExtractions;

      // ---------------------------------------------------------------
      // Cross-Period Analysis
      // ---------------------------------------------------------------
      setPipelinePhase('cross_period');
      addActivity('pipeline', 'Running cross-period analysis...', 'info');

      let crossPeriodFinancial: Record<string, unknown> | null = null;
      let crossPeriodCreditReport: Record<string, unknown> | null = null;
      let crossPeriodBankStatement: Record<string, unknown> | null = null;
      let crossPeriodCorporate: Record<string, unknown> | null = null;

      // Financial cross-period (if >1 statement)
      if (financialExtractions.length > 1) {
        try {
          addActivity('analysis', `Comparing ${financialExtractions.length} financial statements across periods...`, 'info');
          const cpResult = await api.analysis.crossPeriod({
            workflowId: data.workflowId,
            documentType: 'financial_statement',
            extractions: financialExtractions.map((e) => e.data),
            periods: financialExtractions.map((e) => e.period),
          });
          crossPeriodFinancial = cpResult.crossPeriodAnalysis || null;
          addActivity('analysis', 'Financial cross-period analysis complete', 'success');
        } catch (err) {
          console.warn('Financial cross-period analysis failed:', err);
          addActivity('analysis', 'Financial cross-period analysis failed — continuing', 'warning');
        }
      }

      // Credit report cross-period
      if (creditReportExtractions.length > 1) {
        try {
          addActivity('analysis', `Comparing ${creditReportExtractions.length} credit reports...`, 'info');
          const cpResult = await api.analysis.crossPeriod({
            workflowId: data.workflowId,
            documentType: 'credit_report',
            extractions: creditReportExtractions.map((e) => e.data),
            periods: creditReportExtractions.map((e) => e.period),
          });
          crossPeriodCreditReport = cpResult.crossPeriodAnalysis || null;
          addActivity('analysis', 'Credit report cross-period analysis complete', 'success');
        } catch (err) {
          console.warn('Credit report cross-period analysis failed:', err);
          addActivity('analysis', 'Credit report cross-period analysis failed — continuing', 'warning');
        }
      }

      // Bank statement cross-period
      if (bankStatementExtractions.length > 1) {
        try {
          addActivity('analysis', `Comparing ${bankStatementExtractions.length} bank statements...`, 'info');
          const cpResult = await api.analysis.crossPeriod({
            workflowId: data.workflowId,
            documentType: 'bank_statement',
            extractions: bankStatementExtractions.map((e) => e.data),
            periods: bankStatementExtractions.map((e) => e.period),
          });
          crossPeriodBankStatement = cpResult.crossPeriodAnalysis || null;
          addActivity('analysis', 'Bank statement cross-period analysis complete', 'success');
        } catch (err) {
          console.warn('Bank statement cross-period analysis failed:', err);
          addActivity('analysis', 'Bank statement cross-period analysis failed — continuing', 'warning');
        }
      }

      // Corporate search cross-period
      if (corporateExtractions.length > 1) {
        try {
          addActivity('analysis', `Comparing ${corporateExtractions.length} corporate searches...`, 'info');
          const cpResult = await api.analysis.crossPeriod({
            workflowId: data.workflowId,
            documentType: 'corporate_search',
            extractions: corporateExtractions.map((e) => e.data),
            periods: corporateExtractions.map((e) => e.period),
          });
          crossPeriodCorporate = cpResult.crossPeriodAnalysis || null;
          addActivity('analysis', 'Corporate cross-period analysis complete', 'success');
        } catch (err) {
          console.warn('Corporate cross-period analysis failed:', err);
          addActivity('analysis', 'Corporate cross-period analysis failed — continuing', 'warning');
        }
      }

      // Store cross-period results
      const crossPeriodData = {
        financial: crossPeriodFinancial,
        creditReport: crossPeriodCreditReport,
        bankStatement: crossPeriodBankStatement,
        corporate: crossPeriodCorporate,
      };
      updateData({ crossPeriodAnalysis: crossPeriodData });

      // Sync cross-period analysis to backend (fire-and-forget)
      if (data.workflowId) {
        api.workflows.update(data.workflowId, {
          crossPeriodAnalysis: crossPeriodData,
        }).catch((err) =>
          console.warn('Failed to sync crossPeriodAnalysis to backend:', err)
        );
      }

      // ---------------------------------------------------------------
      // Prepare primary data for report generation
      // ---------------------------------------------------------------
      const sortedFinancials = sortByLatestPeriod(financialExtractions);
      const financialData = sortedFinancials.length > 0 ? sortedFinancials[0].data : null;
      const financialUploadId = sortedFinancials.length > 0 ? sortedFinancials[0].documentId : undefined;
      const creditReportData = creditReportExtractions.length > 0 ? creditReportExtractions[0].data : null;
      const creditReportUploadId = creditReportExtractions.length > 0 ? creditReportExtractions[0].documentId : undefined;
      const bankStatementFindings = bankStatementExtractions.length > 0 ? bankStatementExtractions[0].data : null;
      const bankStatementUploadId = bankStatementExtractions.length > 0 ? bankStatementExtractions[0].documentId : undefined;
      const accountData = corporateExtractions.length > 0 ? corporateExtractions[0].data : null;
      const corporateUploadId = corporateExtractions.length > 0 ? corporateExtractions[0].documentId : undefined;

      // Update context with all extracted data
      updateData({
        creditReportData,
        bankStatementFindings,
        accountData,
        creditReportUploadId,
        bankStatementUploadId,
        corporateUploadId,
      });

      // Determine business name
      const businessName =
        data.summaryPreferences?.reportName ||
        financialData?.companyName ||
        creditReportData?.subjectName ||
        bankStatementFindings?.accountInfo?.accountHolder ||
        accountData?.companyName ||
        data.businessName ||
        'Unknown Business';

      // Compute writeup type
      const dataForWriteup = {
        financialData,
        bankStatementFindings,
        selectedCreditReportItems: null,
        accountData,
        summaryPreferences: data.summaryPreferences,
      };
      const computedWriteup = getWriteupType(dataForWriteup);

      // ---------------------------------------------------------------
      // Generate Report
      // ---------------------------------------------------------------
      setPipelinePhase('generating');
      updateData({ workflowState: 'generating' });
      addActivity('report.gen', `Determining writeup type: ${computedWriteup.templateType}`, 'info');
      addActivity('report.gen', 'AI agents synthesizing credit narrative...', 'info');

      let generateResponse: any;
      try {
        generateResponse = await api.reports.generate({
          workflowId: data.workflowId,
          businessName,
          writeupType: computedWriteup.templateType,
          financialData,
          selectedMetrics: data.selectedMetrics || data.summaryPreferences?.selectedDataPoints?.metrics || [],
          customRatios: data.customRatios || [],
          offBalanceSheetRecords: data.offBalanceSheetRecords || {},
          creditReportData,
          selectedCreditReportItems: null,
          accountData,
          bankStatementFindings,
          summaryPreferences: data.summaryPreferences,
          financialUploadId,
          creditReportUploadId,
          bankStatementUploadId,
          corporateUploadId,
          crossPeriodAnalysis: {
            financial: crossPeriodFinancial,
            creditReport: crossPeriodCreditReport,
            bankStatement: crossPeriodBankStatement,
            corporate: crossPeriodCorporate,
          },
          allFinancialExtractions: financialExtractions.map((e) => ({
            data: e.data,
            period: e.period,
            documentId: e.documentId,
          })),
          allCreditReportExtractions: creditReportExtractions.length > 0
            ? creditReportExtractions.map((e) => ({
                data: e.data,
                period: e.period,
                documentId: e.documentId,
              }))
            : undefined,
          allBankStatementExtractions: bankStatementExtractions.length > 0
            ? bankStatementExtractions.map((e) => ({
                data: e.data,
                period: e.period,
                documentId: e.documentId,
              }))
            : undefined,
          allCorporateExtractions: corporateExtractions.length > 0
            ? corporateExtractions.map((e) => ({
                data: e.data,
                period: e.period,
                documentId: e.documentId,
              }))
            : undefined,
        });
      } catch (genError: any) {
        // 409 = already in-flight, treat as non-fatal — SSE/polling will handle it
        if (genError?.statusCode === 409 || genError?.message?.includes('already in progress')) {
          addActivity('report.gen', 'Report generation already in progress — waiting for result', 'info');
        } else {
          throw genError;
        }
      }

      // Handle 200 with already_complete — navigate immediately
      if (generateResponse?.status === 'already_complete' && generateResponse?.analysisId) {
        handleReportComplete(generateResponse.analysisId, generateResponse.reportId);
        return;
      }

      // 202 received — pipeline is running server-side.
      // Start polling fallback in case SSE misses the event.
      reportPollRef.current = setInterval(async () => {
        try {
          const status = await api.reports.status(data.workflowId);
          if ((status.status === 'complete' || status.status === 'completed') && status.analysisId) {
            handleReportComplete(status.analysisId, status.reportId);
          }
        } catch {
          // Polling errors are non-fatal — SSE is the primary channel
        }
      }, 10_000);

    } catch (error: any) {
      console.error('Pipeline failed:', error);
      setPipelinePhase('error');
      setPipelineError(error.message || 'An error occurred during processing');
      closeSse();
      if (reportPollRef.current) clearInterval(reportPollRef.current);
      toast({
        title: 'Processing Failed',
        description: error.message || 'Failed to generate report. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // ---------------------------------------------------------------
  // Safety guard: workflow must exist
  // ---------------------------------------------------------------
  if (!data.workflowId) {
    return (
      <div className="max-w-5xl mx-auto text-center py-20">
        <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Workflow Not Found</h2>
        <p className="text-muted-foreground mb-6">
          No active workflow found. Please start from{' '}
          <Link to="/analyses" className="text-amber-500 hover:text-amber-400 underline font-medium">
            My Analyses
          </Link>.
        </p>
        <Button onClick={() => navigate({ to: '/workflow/summary' })}>
          Go to Summary Preferences
        </Button>
      </div>
    );
  }

  // ---------------------------------------------------------------
  // Render: Processing Dashboard
  // ---------------------------------------------------------------
  return (
    <div className="max-w-5xl mx-auto">
      <div className="space-y-6">
        {/* Header Bar */}
        <Card className="p-6 rounded-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                {pipelinePhase === 'complete' ? 'Complete' : 'Report Generation'}
              </p>
              <h2 className="text-2xl font-bold">
                {pipelinePhase === 'complete' ? (
                  <>Analysis <em>complete</em></>
                ) : pipelinePhase === 'error' ? (
                  'Processing failed'
                ) : (
                  <>Analysis <em>in progress</em></>
                )}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {pipelinePhase === 'complete'
                  ? 'Redirecting to your report...'
                  : pipelinePhase === 'error'
                  ? pipelineError || 'An error occurred'
                  : pipelinePhase === 'waiting_for_docs'
                  ? 'Waiting for remaining documents to finish processing...'
                  : pipelinePhase === 'extracting_rest'
                  ? 'Extracting remaining document data...'
                  : pipelinePhase === 'cross_period'
                  ? 'Running cross-period trend analysis...'
                  : pipelinePhase === 'generating'
                  ? 'AI agents synthesizing credit narrative...'
                  : 'Processing...'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {pipelinePhase !== 'complete' && pipelinePhase !== 'error' && (
                <Loader2 className="w-5 h-5 text-accent-500 animate-spin" />
              )}
              {pipelinePhase === 'complete' && (
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              )}
              {pipelinePhase === 'error' && (
                <XCircle className="w-6 h-6 text-red-500" />
              )}
              <span className="text-sm font-medium text-muted-foreground">
                {documentStatuses.filter(d => d.status === 'completed').length}/{documentStatuses.length} docs
              </span>
            </div>
          </div>

          {/* Overall Progress Bar */}
          {pipelinePhase !== 'error' && documentStatuses.length > 0 && (
            <div className="mt-4">
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-500',
                    pipelinePhase === 'complete' ? 'bg-emerald-500' : 'bg-accent-500',
                  )}
                  style={{
                    width: pipelinePhase === 'complete'
                      ? '100%'
                      : pipelinePhase === 'generating'
                      ? '92%'
                      : pipelinePhase === 'cross_period'
                      ? '80%'
                      : pipelinePhase === 'extracting_rest'
                      ? '60%'
                      : pipelinePhase === 'waiting_for_docs'
                      ? `${documentStatuses.length > 0 ? (documentStatuses.filter(d => d.status === 'completed' || d.status === 'failed').length / documentStatuses.length) * 50 : 10}%`
                      : '50%',
                  }}
                />
              </div>
            </div>
          )}
        </Card>

        {/* Two-Column Layout: Documents + Activity Log */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Per-Document Extraction Status */}
          <Card className="p-6 rounded-2xl">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Per-Document Extraction
            </h3>
            <div className="space-y-2">
              {documentStatuses.map((doc) => {
                const integrity = doc.integrityReport;
                const isExpanded = expandedIntegrity.has(doc.id);
                const toggleExpand = () => {
                  setExpandedIntegrity((prev) => {
                    const next = new Set(prev);
                    if (next.has(doc.id)) next.delete(doc.id);
                    else next.add(doc.id);
                    return next;
                  });
                };

                return (
                  <div key={doc.id}>
                    <div className="flex items-center gap-3 py-2">
                      {doc.status === 'completed' ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      ) : doc.status === 'failed' ? (
                        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      ) : doc.status === 'processing' ? (
                        <Loader2 className="w-5 h-5 text-accent-500 animate-spin flex-shrink-0" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          'text-sm font-medium truncate',
                          doc.status === 'failed' && 'text-red-500',
                        )}>
                          {doc.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {doc.status === 'completed' ? 'extracted · source-traced'
                            : doc.status === 'failed' ? 'failed'
                            : doc.status === 'processing' ? 'processing...'
                            : 'queued'}
                        </p>
                      </div>
                      {/* Integrity Badge */}
                      {integrity && doc.status === 'completed' && (
                        <button
                          type="button"
                          onClick={toggleExpand}
                          className={cn(
                            'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 transition-colors',
                            integrity.verdict === 'clean' && 'bg-emerald-500/10 text-emerald-600',
                            integrity.verdict === 'warning' && 'bg-amber-500/10 text-amber-600',
                            integrity.verdict === 'alert' && 'bg-red-500/10 text-red-600',
                          )}
                        >
                          {integrity.verdict === 'clean' ? (
                            <ShieldCheck className="w-3.5 h-3.5" />
                          ) : integrity.verdict === 'warning' ? (
                            <AlertTriangle className="w-3.5 h-3.5" />
                          ) : (
                            <ShieldAlert className="w-3.5 h-3.5" />
                          )}
                          {integrity.verdict === 'clean'
                            ? 'clean'
                            : `${integrity.flags.length} flag${integrity.flags.length !== 1 ? 's' : ''}`}
                          {integrity.flags.length > 0 && (
                            isExpanded
                              ? <ChevronDown className="w-3 h-3" />
                              : <ChevronRight className="w-3 h-3" />
                          )}
                        </button>
                      )}
                    </div>

                    {/* Expandable Integrity Flags Panel */}
                    {isExpanded && integrity && integrity.flags.length > 0 && (
                      <div className="ml-8 mb-2 p-3 rounded-lg bg-muted/50 border border-border/50 space-y-1.5">
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Confidence: {(integrity.avgConfidence * 100).toFixed(1)}%
                        </p>
                        {integrity.flags.map((flag, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'flex items-start gap-2 text-xs leading-relaxed',
                              flag.severity === 'high' && 'text-red-500',
                              flag.severity === 'medium' && 'text-amber-500',
                              flag.severity === 'low' && 'text-muted-foreground',
                            )}
                          >
                            <span className="flex-shrink-0 mt-0.5">
                              {flag.severity === 'high' ? '●' : flag.severity === 'medium' ? '◐' : '○'}
                            </span>
                            <span>{flag.message}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {documentStatuses.length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Waiting for document status...
                </p>
              )}
            </div>
          </Card>

          {/* Right Column: Live Activity Log */}
          <Card className="p-6 rounded-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-accent-500" />
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Agent Activity
              </h3>
              {pipelinePhase !== 'complete' && pipelinePhase !== 'error' && (
                <span className="text-xs text-accent-500 font-medium ml-auto">LIVE</span>
              )}
            </div>
            <div
              ref={activityLogRef}
              className="space-y-1 max-h-[400px] overflow-y-auto font-mono text-xs"
            >
              {activityLog.map((entry) => {
                const time = new Date(entry.timestamp);
                const mm = String(time.getMinutes()).padStart(2, '0');
                const ss = String(time.getSeconds()).padStart(2, '0');
                return (
                  <div
                    key={entry.id}
                    className={cn(
                      'flex gap-3 py-1 leading-relaxed',
                      entry.level === 'error' && 'text-red-400',
                      entry.level === 'warning' && 'text-amber-400',
                      entry.level === 'success' && 'text-emerald-400',
                      entry.level === 'info' && 'text-muted-foreground',
                    )}
                  >
                    <span className="text-muted-foreground/60 flex-shrink-0 tabular-nums">
                      {mm}:{ss}
                    </span>
                    <span>
                      <span className="font-semibold">{entry.source}:</span>{' '}
                      {entry.message}
                    </span>
                  </div>
                );
              })}
              {activityLog.length === 0 && (
                <p className="text-muted-foreground py-4 text-center font-sans text-sm">
                  Waiting for activity...
                </p>
              )}
            </div>
          </Card>
        </div>

        {/* Error retry button */}
        {pipelinePhase === 'error' && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => {
                setPipelineError(null);
                pipelineStartedRef.current = false;
                reportNavigatedRef.current = false;
                // Clear dedup set for non-FS docs so retry re-sends
                const nonFsDocs = documentStatuses.filter(
                  (d) => d.documentType !== 'financial_statement' && d.status === 'completed'
                );
                for (const d of nonFsDocs) extractedDocIdsGlobal.delete(d.id);
                // Re-trigger the pipeline via the useEffect
                setPipelinePhase('waiting_for_docs');
              }}
            >
              Retry
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
